// API Service to connect frontend with backend
import { syncService } from './syncService';

// Detect platforms
const isElectron = typeof window !== 'undefined' && (window as any).process?.type === 'renderer';

// More aggressive Capacitor detection for Android
const isCapacitor = typeof window !== 'undefined' && (window as any).Capacitor && (
    (window as any).Capacitor.isNativePlatform ||
    window.location.href.includes('capacitor://') ||
    ((window as any).Capacitor.getPlatform?.() !== 'web' && (window as any).Capacitor.getPlatform?.() !== undefined) ||
    (window as any).Capacitor.getPlatform?.() === 'android' ||
    (window as any).Capacitor.getPlatform?.() === 'ios'
);

// API URL logic:
// - In Electron: Always connect to localhost:5000 (embedded server)
// - In Capacitor (Android): Connect to Cloud Production Server (with fallback)
// - In Dev (Vite): Connect to localhost:5000 (separate server)
// - In Production Web: Use relative path '/api' (same origin)
const CLOUD_BACKUP_URL = 'https://gambitafood.onrender.com/api';
const getPrimaryUrl = () => {
    // Use non-www URL directly — www.gombitafood.store returns a 301 redirect
    // that native iOS Capacitor apps cannot follow, causing API calls to fail.
    return 'https://gombitafood.store/api';
};

// Clear cached www. URL if it exists — iOS can't follow the 301 redirect
if (typeof window !== 'undefined') {
    const cachedUrl = localStorage.getItem('active_cloud_url');
    if (cachedUrl && cachedUrl.includes('www.')) {
        localStorage.removeItem('active_cloud_url');
    }
}

export let serverClientOffset = 0;

export let activeCloudUrl = typeof window !== 'undefined'
    ? (localStorage.getItem('active_cloud_url') || getPrimaryUrl())
    : getPrimaryUrl();



export let API_BASE_URL = (() => {
    if (typeof window === 'undefined') return 'http://localhost:5000/api';
    const host = window.location.hostname || 'localhost';
    const isDevPort = window.location.port === '5173' || window.location.port === '3000';
    if (isElectron || isDevPort) {
        return `http://${host}:5000/api`;
    }
    if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
    }
    if (isCapacitor) return activeCloudUrl;
    // Production web: use relative path so nginx proxy works correctly
    return '/api';
})();

const updateApiBaseUrl = () => {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname || 'localhost';
    const isDevPort = window.location.port === '5173' || window.location.port === '3000';
    if (isElectron || isDevPort) {
        API_BASE_URL = `http://${host}:5000/api`;
    } else if (import.meta.env.VITE_API_URL) {
        API_BASE_URL = import.meta.env.VITE_API_URL;
    } else if (isCapacitor) {
        API_BASE_URL = activeCloudUrl;
    } else {
        // Production web: use relative path so nginx proxy works correctly
        API_BASE_URL = '/api';
    }
};

export const switchToBackupUrl = () => {
    if (activeCloudUrl !== CLOUD_BACKUP_URL) {
        console.log('⚠️ Primary URL failed. Switching to Backup URL:', CLOUD_BACKUP_URL);
        activeCloudUrl = CLOUD_BACKUP_URL;
        if (typeof window !== 'undefined') {
            localStorage.setItem('active_cloud_url', CLOUD_BACKUP_URL);
        }
        updateApiBaseUrl();
    }
};

export const switchToPrimaryUrl = () => {
    const primary = getPrimaryUrl();
    if (activeCloudUrl !== primary) {
        console.log('🔄 Resetting to Primary URL:', primary);
        activeCloudUrl = primary;
        if (typeof window !== 'undefined') {
            localStorage.setItem('active_cloud_url', primary);
        }
        updateApiBaseUrl();
    }
};

export const getActiveCloudSocketUrl = () => {
    return activeCloudUrl.replace(/\/api$/, '');
};

console.log('API_BASE_URL initialized as:', API_BASE_URL);
console.log('Platform:', isElectron ? 'Electron' : isCapacitor ? 'Capacitor (iOS/Android)' : 'Web');
console.log('Capacitor object:', (window as any).Capacitor);
console.log('Capacitor platform:', (window as any).Capacitor?.getPlatform?.());
console.log('isNativePlatform:', (window as any).Capacitor?.isNativePlatform);
console.log('Window href:', typeof window !== 'undefined' ? window.location.href : 'N/A');

// Initialize syncService with a dynamic getter for API base URL
syncService.setApiBaseUrl(() => API_BASE_URL);

// Background check to restore primary domain if it becomes available again
if (isCapacitor && activeCloudUrl === CLOUD_BACKUP_URL) {
    const primaryUrl = getPrimaryUrl();
    fetch(`${primaryUrl}/tables`)
        .then(() => {
            console.log('✅ Primary URL is reachable again. Switching back.');
            switchToPrimaryUrl();
        })
        .catch(() => {
            console.log('ℹ️ Primary URL is still unreachable. Remaining on backup.');
        });
}

class APIService {
    private currentBranchId: string | null = (() => {
        const stored = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
        if (stored) {
            try {
                const user = JSON.parse(stored);
                if (user.role === 'livreur') return null;
                if (user.branchId) return user.branchId;
            } catch (e) { }
        }
        return 'gombita-1';
    })();

    setBranchId(branchId: string | null) {
        const stored = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
        if (stored) {
            try {
                const user = JSON.parse(stored);
                if (user.role === 'livreur') {
                    this.currentBranchId = null;
                    return;
                }
            } catch (e) { }
        }
        this.currentBranchId = branchId;
    }

    private mutationQueue: Promise<any> = Promise.resolve();

    private async enqueueMutation<T>(fn: () => Promise<T>): Promise<T> {
        const result = this.mutationQueue.then(fn);
        this.mutationQueue = result.catch(() => { });
        return result;
    }

    // Helper method for fetch requests
    private async request(endpoint: string, options: RequestInit = {}) {
        const token = localStorage.getItem('token');
        let baseUrl = API_BASE_URL;
        let url = `${baseUrl}${endpoint}`;

        // Append branchId if set
        if (this.currentBranchId) {
            const separator = url.includes('?') ? '&' : '?';
            url += `${separator}branchId=${this.currentBranchId}`;
        }

        // Append cache buster to prevent aggressive mobile webview caching
        const cacheBuster = `_cb=${Date.now()}`;
        url += url.includes('?') ? `&${cacheBuster}` : `?${cacheBuster}`;

        try {
            const response = await fetch(url, {
                ...options,
                cache: 'no-store', // explicitly disable cache
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    ...options.headers,
                },
            });

            const serverDate = response.headers.get('Date');
            if (serverDate) {
                const parsedServerTime = Date.parse(serverDate);
                if (!isNaN(parsedServerTime)) {
                    serverClientOffset = Date.now() - parsedServerTime;
                }
            }

            if (!response.ok) {
                if (response.status === 401 && !endpoint.includes('/login')) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    if (typeof window !== 'undefined') {
                        window.location.href = '/';
                    }
                }
                let errorMessage = `API Error: ${response.status} ${response.statusText}`;
                let errorObj: any = new Error(errorMessage);
                try {
                    const errorData = await response.json();
                    console.error('API Error Details:', errorData);
                    if (errorData && errorData.error) {
                        errorObj.message = errorData.error;
                    } else if (errorData && errorData.message) {
                        errorObj.message = errorData.message;
                    }
                    Object.assign(errorObj, errorData);
                } catch (e) {
                    console.error('Failed to parse error response:', e);
                }
                throw errorObj;
            }

            const data = await response.json();
            return this.normalize(data);

        } catch (error: any) {
            const primary = getPrimaryUrl();
            // Fallback check: if the request failed on primary URL due to network error, switch to backup and retry
            if (isCapacitor && baseUrl.includes('gombitafood.store') && (error instanceof TypeError || error.message?.includes('Failed to fetch') || error.name === 'TypeError')) {
                console.warn('⚠️ Network request to primary URL failed. Retrying with backup URL...', error);
                switchToBackupUrl();

                // Re-evaluate URL with the backup
                baseUrl = API_BASE_URL;
                url = `${baseUrl}${endpoint}`;
                if (this.currentBranchId) {
                    const separator = url.includes('?') ? '&' : '?';
                    url += `${separator}branchId=${this.currentBranchId}`;
                }

                // Append cache buster to prevent aggressive mobile webview caching
                const cacheBusterFallback = `_cb=${Date.now()}`;
                url += url.includes('?') ? `&${cacheBusterFallback}` : `?${cacheBusterFallback}`;

                const response = await fetch(url, {
                    ...options,
                    cache: 'no-store', // explicitly disable cache
                    headers: {
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                        ...options.headers,
                    },
                });

                if (!response.ok) {
                    let errorMessage = `API Error: ${response.statusText}`;
                    let errorObj: any = new Error(errorMessage);
                    try {
                        const errorData = await response.json();
                        if (errorData && errorData.message) {
                            errorObj.message = errorData.message;
                        }
                        Object.assign(errorObj, errorData);
                    } catch (e) { }
                    throw errorObj;
                }

                const data = await response.json();
                return this.normalize(data);
            }
            throw error;
        }
    }

    // Recursively convert _id to id for frontend compatibility
    private normalize(data: any): any {
        if (!data || typeof data !== 'object') return data;

        if (Array.isArray(data)) {
            return data.map(item => this.normalize(item));
        }

        const normalized: any = { ...data };

        // Handle MongoDB _id
        if (normalized._id) {
            normalized.id = normalized._id.toString();
        }

        // Special handling for Tables (mapping 'number' to 'id')
        if (normalized.number !== undefined && normalized.status !== undefined) {
            normalized.id = normalized.number;
        }

        // Also normalize nested objects/arrays if any
        for (const key in normalized) {
            if (normalized[key] && typeof normalized[key] === 'object') {
                normalized[key] = this.normalize(normalized[key]);
            }
        }

        return normalized;
    }

    // Users
    async login(username: string, password: string) {
        return this.request('/users/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
        });
    }

    async getUsers() {
        return this.request(`/users?t=${new Date().getTime()}`);
    }

    async createUser(user: any) {
        return this.request('/users', {
            method: 'POST',
            body: JSON.stringify(user),
        });
    }

    async updateUser(id: string, user: any) {
        return this.request(`/users/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(user),
        });
    }

    async deleteUser(id: string) {
        return this.request(`/users/${id}`, {
            method: 'DELETE',
        });
    }

    async verifyAdminPin(pin: string) {
        return this.request('/users/verify-admin-pin', {
            method: 'POST',
            body: JSON.stringify({ pin }),
        });
    }

    async unlockUser(username: string) {
        return this.request(`/users/unlock/${encodeURIComponent(username.toLowerCase())}`, {
            method: 'POST',
        });
    }

    async unlockAllUsers() {
        return this.request('/users/unlock-all', {
            method: 'POST',
        });
    }

    async updateLivreurLocation(location: { lat?: number; lng?: number; isOnline?: boolean }) {
        return this.request('/users/location', {
            method: 'POST',
            body: JSON.stringify(location),
        });
    }

    async confirmLivreurOrder(id: string, livreurId: string, livreurName: string) {
        const url = `/orders/${id}/confirm-livreur`;
        return this.request(url, {
            method: 'POST',
            body: JSON.stringify({ livreurId, livreurName }),
        });
    }

    async printLivreurOrder(id: string, livreurName: string, branchId?: string) {
        let url = `/orders/${id}/print-livreur`;
        if (branchId) url += `?branchId=${branchId}`;
        return this.request(url, {
            method: 'POST',
            body: JSON.stringify({ livreurName }),
        });
    }

    async rejectLivreurOrder(id: string, livreurId: string, branchId?: string) {
        let url = `/orders/${id}/reject-livreur`;
        if (branchId) url += `?branchId=${branchId}`;
        return this.request(url, {
            method: 'POST',
            body: JSON.stringify({ livreurId })
        });
    }

    // Menu Items
    async getMenuItems() {
        const branchIdParam = this.currentBranchId ? `&branchId=${this.currentBranchId}` : '';
        return this.request(`/menu?t=${new Date().getTime()}${branchIdParam}`);
    }

    async createMenuItem(item: any) {
        return this.request('/menu', {
            method: 'POST',
            body: JSON.stringify(item),
        });
    }

    async updateMenuItem(id: string, item: any) {
        return this.request(`/menu/${id}`, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
    }

    async deleteMenuItem(id: string) {
        return this.request(`/menu/${id}`, {
            method: 'DELETE',
        });
    }

    // Addons
    async getAddons() {
        return this.request(`/addons?t=${new Date().getTime()}`);
    }

    async createAddon(addon: any) {
        return this.request('/addons', {
            method: 'POST',
            body: JSON.stringify(addon),
        });
    }

    async updateAddon(id: string, addon: any) {
        return this.request(`/addons/${id}`, {
            method: 'PUT',
            body: JSON.stringify(addon),
        });
    }

    async deleteAddon(id: string) {
        return this.request(`/addons/${id}`, {
            method: 'DELETE',
        });
    }

    // Tables
    async getTables() {
        return this.request(`/tables?t=${new Date().getTime()}`);
    }

    // --- Blacklist Management ---
    async getBlacklist() {
        return this.request('/api/blacklist');
    }

    async banClient(name: string, phone: string, reason: string = 'طلبيات وهمية متكررة (التمسخير)', bannedBy: string = 'Caissier') {
        return this.request('/api/blacklist/ban', {
            method: 'POST',
            body: JSON.stringify({ name, phone, reason, bannedBy }),
        });
    }

    async unbanClient(id: string) {
        return this.request(`/api/blacklist/${id}`, {
            method: 'DELETE',
        });
    }
    // ----------------------------

    async updateOrderItems(id: string, updates: { items: any[], subtotal: number, deliveryFee?: number, total: number, paymentMethod?: string }) {
        return this.request(`/orders/${id}/items`, { method: 'PUT', body: JSON.stringify(updates) });
    }

    async markOrderPrinted(id: string) {
        return this.request(`/orders/${id}/mark-printed`, { method: 'PUT' });
    }

    async createTable() {
        return this.request('/tables', {
            method: 'POST',
        });
    }

    async updateTableStatus(tableNumber: number, status: 'available' | 'occupied') {
        return this.request(`/tables/${tableNumber}`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        });
    }

    async deleteTableLast() {
        return this.request('/tables/last', {
            method: 'DELETE',
        });
    }

    async createSafariSlot() {
        return this.request('/tables/safari', {
            method: 'POST',
        });
    }

    async deleteSafariSlotLast() {
        return this.request('/tables/safari/last', {
            method: 'DELETE',
        });
    }

    async deleteTableByNumber(number: number) {
        return this.request(`/tables/${number}`, {
            method: 'DELETE',
        });
    }

    // Promo Banner
    async getPromoBanner(storeId?: string) {
        const url = storeId ? `/promo-banner?store_id=${storeId}` : '/promo-banner';
        console.log('Fetching promo banner with URL:', url, 'storeId:', storeId);
        return this.request(url);
    }

    async updatePromoBanner(bannerData: any) {
        return this.request('/promo-banner', {
            method: 'PUT',
            body: JSON.stringify(bannerData),
        });
    }

    async deletePromoBanner(storeId?: string) {
        const url = storeId ? `/promo-banner?store_id=${storeId}` : '/promo-banner';
        return this.request(url, {
            method: 'DELETE',
        });
    }

    async uploadImage(file: File): Promise<{ imageUrl: string; filename: string }> {
        const formData = new FormData();
        formData.append('image', file);

        const baseUrl = API_BASE_URL;
        const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

        const response = await fetch(`${baseUrl}/upload/image`, {
            method: 'POST',
            headers: {
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Upload failed');
        }

        return response.json();
    }

    // Orders
    async getOrders() {
        const stored = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
        if (stored) {
            try {
                const user = JSON.parse(stored);
                if (user.role === 'livreur') {
                    const [orders1, orders2] = await Promise.all([
                        this.request(`/orders?t=${new Date().getTime()}&branchId=gombita-1`),
                        this.request(`/orders?t=${new Date().getTime()}&branchId=gombita-2`)
                    ]);
                    const o1 = (orders1 || []).map((o: any) => ({ ...o, branchId: o.branchId || 'gombita-1' }));
                    const o2 = (orders2 || []).map((o: any) => ({ ...o, branchId: o.branchId || 'gombita-2' }));
                    const allOrders = [...o1, ...o2];
                    return Array.from(new Map(allOrders.map((o: any) => [o.id || o._id, o])).values());
                }
            } catch (e) { }
        }
        return this.request(`/orders?t=${new Date().getTime()}`);
    }

    async createOrder(order: any) {
        // Offline-First: if offline, queue and return optimistic placeholder
        if (!syncService.isOnline) {
            const tempId = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const cachedOrders = JSON.parse(localStorage.getItem('cache_orders') || '[]');
            const maxTicket = cachedOrders.length > 0
                ? Math.max(...cachedOrders.map((o: any) => o.ticketNumber || 0))
                : 0;
            const optimisticOrder = {
                ...order,
                _id: tempId,
                id: tempId,
                ticketNumber: maxTicket + 1,
                timestamp: new Date().toISOString(),
                status: 'pending',
                _offline: true,
            };
            // Persist optimistic order to cache immediately
            localStorage.setItem('cache_orders', JSON.stringify([...cachedOrders, optimisticOrder]));
            // Queue the real API call for when we come back online
            await syncService.executeMutation({
                url: '/orders',
                method: 'POST',
                body: order,
                description: `New order for table ${order.tableId}`,
                branchId: this.currentBranchId,
                optimisticResponse: optimisticOrder,
            });
            return optimisticOrder;
        }
        return this.enqueueMutation(() => this.request('/orders', {
            method: 'POST',
            body: JSON.stringify(order),
        }));
    }

    async updateOrder(id: string, update: any, branchId?: string) {
        let url = `/orders/${id}`;
        if (branchId) url += `?branchId=${branchId}`;
        // Offline-First: queue update if offline
        if (!syncService.isOnline) {
            return syncService.executeMutation({
                url,
                method: 'PATCH',
                body: update,
                description: `Update order ${id}`,
                branchId: this.currentBranchId,
                optimisticResponse: { ...update, id, _offline: true },
            });
        }
        return this.enqueueMutation(() => this.request(url, {
            method: 'PATCH',
            body: JSON.stringify(update),
        }));
    }

    async rateOrder(id: string, restaurantRating: number, livreurRating: number) {
        return this.request(`/orders/${id}/rate`, {
            method: 'POST',
            body: JSON.stringify({ restaurantRating, livreurRating })
        });
    }

    async deleteOrder(id: string, user?: string) {
        let url = `/orders/${id}`;
        if (user) url += `?user=${encodeURIComponent(user)}`;
        // Offline-First: queue delete if offline
        if (!syncService.isOnline) {
            return syncService.executeMutation({
                url,
                method: 'DELETE',
                description: `Delete order ${id}`,
                branchId: this.currentBranchId,
                optimisticResponse: { success: true, _offline: true },
            });
        }
        return this.enqueueMutation(() => this.request(url, {
            method: 'DELETE',
        }));
    }

    async deleteAllOrders() {
        return this.enqueueMutation(() => this.request('/orders/actions/delete-all', {
            method: 'DELETE',
        }));
    }

    async dailyReset() {
        return this.enqueueMutation(() => this.request('/orders/actions/daily-reset', {
            method: 'POST',
        }));
    }

    async processPayment(tableId: number, caissierName: string, orderIds?: string[]) {
        return this.enqueueMutation(() => this.request('/orders/payment', {
            method: 'POST',
            body: JSON.stringify({ tableId, caissierName, orderIds }),
        }));
    }

    // Stock
    async getStock() {
        return this.request(`/stock?t=${new Date().getTime()}`);
    }

    async createStockItem(item: any) {
        return this.request('/stock', {
            method: 'POST',
            body: JSON.stringify(item),
        });
    }

    async updateStock(id: string, update: any) {
        return this.request(`/stock/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(update),
        });
    }

    async deleteStockItem(id: string) {
        return this.request(`/stock/${id}`, {
            method: 'DELETE',
        });
    }

    // Suppliers
    async getSuppliers() {
        return this.request(`/suppliers?t=${new Date().getTime()}`);
    }

    async createSupplier(supplier: any) {
        return this.request('/suppliers', {
            method: 'POST',
            body: JSON.stringify(supplier),
        });
    }

    async updateSupplier(id: string, data: any) {
        return this.request(`/suppliers/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteSupplier(id: string) {
        return this.request(`/suppliers/${id}`, {
            method: 'DELETE',
        });
    }

    // Employees
    async getEmployees() {
        return this.request(`/employees?t=${new Date().getTime()}`);
    }

    async createEmployee(employee: any) {
        return this.request('/employees', {
            method: 'POST',
            body: JSON.stringify(employee),
        });
    }

    async updateEmployee(id: string, data: any) {
        return this.request(`/employees/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteEmployee(id: string) {
        return this.request(`/employees/${id}`, {
            method: 'DELETE',
        });
    }

    async markAttendance(id: string, date: Date, present: boolean) {
        // Legacy support if needed, but we should use specific endpoints
        return this.request(`/employees/${id}/attendance`, {
            method: 'POST',
            body: JSON.stringify({ date, present }),
        });
    }

    async markAbsent(id: string, date: Date, note: string) {
        return this.request(`/employees/${id}/mark-absent`, {
            method: 'POST',
            body: JSON.stringify({ date, note }),
        });
    }

    async markPresent(id: string) {
        return this.request(`/employees/${id}/mark-present`, {
            method: 'POST',
        });
    }

    async addAdvance(id: string, amount: number, note: string) {
        return this.request(`/employees/${id}/advance`, {
            method: 'POST',
            body: JSON.stringify({ amount, note }),
        });
    }

    async payEmployee(id: string) {
        return this.request(`/employees/${id}/pay`, {
            method: 'POST',
        });
    }

    async getEmployeeReports() {
        return this.request('/reports/employees');
    }

    // Expenses
    async getExpenses() {
        return this.request(`/expenses?t=${new Date().getTime()}`);
    }

    async createExpense(expense: any) {
        return this.request('/expenses', {
            method: 'POST',
            body: JSON.stringify(expense),
        });
    }

    // Reports
    async getReports(startDate?: Date, endDate?: Date) {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate.toISOString());
        if (endDate) params.append('endDate', endDate.toISOString());
        return this.request(`/reports?${params.toString()}`);
    }
    private getLocalBridgeUrl() {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('local_bridge_url') || 'http://localhost:5000';
        }
        return 'http://localhost:5000';
    }

    private formatPrintData(order: any, printer: any, title?: string) {
        const cachedAddons = JSON.parse(localStorage.getItem('cache_addons') || '[]');
        const addonMap = new Map(cachedAddons.map((a: any) => [a.id || a._id, a]));

        const processedItems = (order.items || []).map((i: any) => {
            const resolvedAddons = (i.selectedAddons || []).map((idOrName: string) => {
                const addon = addonMap.get(idOrName);
                if (addon) return addon.name;
                return idOrName;
            });

            let mixAddons = [...(i.mixAddons || [])];
            if (i.paidAddonsWithQty) {
                const entries = typeof i.paidAddonsWithQty.entries === 'function'
                    ? Array.from(i.paidAddonsWithQty.entries())
                    : Object.entries(i.paidAddonsWithQty);
                entries.forEach(([id, qty]) => {
                    const addon = addonMap.get(id);
                    if (addon) {
                        mixAddons.push(`${addon.name} x${qty}`);
                    }
                });
            } else if (i.paidAddons) {
                (i.paidAddons || []).forEach((id: string) => {
                    const addon = addonMap.get(id);
                    if (addon) {
                        mixAddons.push(addon.name);
                    }
                });
            }

            return {
                name: i.name,
                quantity: i.quantity,
                price: i.price,
                breadType: i.breadType,
                selectedAddons: resolvedAddons,
                mixAddons: mixAddons,
                toppings: i.toppings || [],
                ingredients: i.ingredients || [],
                customIngredients: i.customIngredients || [],
                plateType: i.plateType || null,
                notes: i.notes || ''
            };
        });

        const getItemDetailIdentity = (item: any) => {
            const safe = (obj: any) => JSON.stringify(obj);
            const addonsCmp = [...(item.selectedAddons || [])].sort();
            const ingCmp = [...(item.ingredients || [])].sort();
            const mixCmp = [...(item.mixAddons || [])].sort();
            const toppingsCmp = [...(item.toppings || [])].sort();
            const paidAddonsCmp = [...(item.paidAddons || [])].sort();
            const paidQtyCmp = item.paidAddonsWithQty || {};
            return `${item.name}-${item.price || 0}-${item.breadType || ''}-${item.notes || ''}-${item.plateType || ''}-${safe(addonsCmp)}-${safe(ingCmp)}-${safe(mixCmp)}-${safe(toppingsCmp)}-${safe(paidAddonsCmp)}-${safe(paidQtyCmp)}-${safe(item.customIngredients || [])}`;
        };

        const groupedItems: any[] = [];
        processedItems.forEach((item: any) => {
            const identity = getItemDetailIdentity(item);
            const existing = groupedItems.find(g => getItemDetailIdentity(g) === identity);
            if (existing) {
                existing.quantity += item.quantity;
            } else {
                groupedItems.push({ ...item });
            }
        });

        return {
            items: groupedItems,
            title: title || printer.name,
            ticketNumber: order.ticketNumber ? String(order.ticketNumber) : "000",
            tableId: order.tableId,
            serveur: order.serveurName || order.caissierName || '-',
            type: order.type || 'dine-in',
            clientName: order.clientInfo?.name || '',
            clientPhone: order.clientInfo?.phone || '',
            clientAddress: order.clientInfo?.address || '',
            notes: order.notes || '',
            total: order.total
        };
    }

    private getPrintersForItems(items: any[]): any[] {
        const cachedPrinters = JSON.parse(localStorage.getItem('cached_printers') || '[]');
        if (cachedPrinters.length === 0) return [];

        const matchedPrinters: any[] = [];
        cachedPrinters.forEach((p: any) => {
            if (!p.enabled) return;
            const types = (p.productTypes || []).map((t: string) => t.toLowerCase().trim());
            if (types.includes('total') && types.length === 1 && !types.includes('all')) return;

            const matches = items.some(i => {
                if (types.includes('all')) return true;
                const mainType = (i.type || '').toLowerCase().trim();
                const plateType = (i.plateType || '').toLowerCase().trim();
                const itemName = (i.name || '').toLowerCase().trim();

                if (mainType === 'drink' || mainType === 'boisson') {
                    return types.includes('drink') || types.includes('boisson') || types.includes('bar');
                }

                const isShawarma = mainType === 'shawarma' || plateType === 'shawarma' || itemName.includes('shawarma');
                const isSandwich = mainType === 'sandwich' || plateType === 'sandwich' || itemName.includes('sandwich');
                const isMix = mainType === 'mix' || itemName.includes('mix');
                const isPlate = mainType === 'plate' || mainType === 'plat' || itemName.includes('plat');
                const isPizza = mainType === 'pizza' || plateType === 'pizza' || itemName.includes('pizza');

                if (isPizza) return types.includes('pizza');
                if (isPlate) {
                    if (isShawarma) return types.includes('shawarma');
                    if (isMix) return types.includes('sandwich');
                    if (plateType === 'sandwich') return types.includes('sandwich');
                    return types.includes('plat') || types.includes('plate') || (types.includes('shawarma') && isShawarma);
                }
                if (types.includes('shawarma') && isShawarma) return true;
                if (types.includes('sandwich') && (isSandwich || isMix) && !isShawarma) return true;

                return types.includes('food') || types.includes('kitchen') || types.includes('cuisine') || types.includes(mainType);
            });

            if (matches) {
                matchedPrinters.push(p);
            }
        });

        return matchedPrinters;
    }

    private async fallbackPrintOrder(printData: any) {
        const { orderId, itemsToPrint, title } = printData;
        const cachedOrders = JSON.parse(localStorage.getItem('cache_orders') || '[]');
        const order = cachedOrders.find((o: any) => o.id === orderId || o._id === orderId);

        if (!order) {
            return { success: false, error: "Order not found in cache for offline printing" };
        }

        const items = itemsToPrint || order.items;
        const printers = this.getPrintersForItems(items);

        if (printers.length === 0) {
            return { success: false, error: "No printers matched or cached" };
        }

        const localBridgeUrl = this.getLocalBridgeUrl();
        const results = [];
        let successCount = 0;

        for (const p of printers) {
            try {
                const formatted = this.formatPrintData({ ...order, items }, p, title || p.name);
                const response = await fetch(`${localBridgeUrl}/print`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ printer: p, data: formatted }),
                    mode: 'cors'
                });
                if (response.ok) {
                    results.push({ printer: p.name, success: true });
                    successCount++;
                } else {
                    results.push({ printer: p.name, success: false, error: "Bridge refused print job" });
                }
            } catch (bridgeErr: any) {
                results.push({ printer: p.name, success: false, error: bridgeErr.message });
            }
        }

        return {
            success: successCount > 0,
            results,
            message: `Offline print completed: ${successCount}/${printers.length} succeeded`
        };
    }

    private async fallbackPrintInvoice(invoiceData: any) {
        const cachedPrinters = JSON.parse(localStorage.getItem('cached_printers') || '[]');
        const totalPrinter = cachedPrinters.find((p: any) =>
            p.enabled && (p.productTypes || []).map((t: string) => t.toLowerCase()).includes('total')
        ) || cachedPrinters[0];

        if (!totalPrinter) {
            return { success: false, error: "No invoice/total printer cached" };
        }

        const localBridgeUrl = this.getLocalBridgeUrl();
        try {
            const formatted = {
                items: invoiceData.items || [],
                title: invoiceData.title || "FACTURE",
                ticketNumber: invoiceData.ticketNumber ? String(invoiceData.ticketNumber) : "000",
                tableId: invoiceData.tableId,
                serveur: invoiceData.serveur || '-',
                type: invoiceData.type || 'dine-in',
                clientName: invoiceData.clientName || '',
                clientPhone: invoiceData.clientPhone || '',
                clientAddress: invoiceData.clientAddress || '',
                livreurName: invoiceData.livreurName || undefined,
                notes: invoiceData.notes || '',
                total: invoiceData.total,
                isInvoice: true,
                openDrawer: invoiceData.openDrawer !== false
            };

            const response = await fetch(`${localBridgeUrl}/print`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ printer: totalPrinter, data: formatted }),
                mode: 'cors'
            });

            if (response.ok) {
                return { success: true, results: [{ printer: totalPrinter.name, success: true }] };
            }
            throw new Error("Bridge refused invoice print job");
        } catch (bridgeErr: any) {
            return { success: false, error: bridgeErr.message };
        }
    }

    private async fallbackOpenDrawer() {
        const cachedPrinters = JSON.parse(localStorage.getItem('cached_printers') || '[]');
        const totalPrinter = cachedPrinters.find((p: any) =>
            p.enabled && (p.productTypes || []).map((t: string) => t.toLowerCase()).includes('total')
        ) || cachedPrinters[0];

        if (!totalPrinter) {
            return { success: false, error: "No printers cached for open drawer" };
        }

        const localBridgeUrl = this.getLocalBridgeUrl();
        try {
            const response = await fetch(`${localBridgeUrl}/print`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    printer: totalPrinter,
                    data: { openDrawer: true, onlyDrawer: true, items: [] }
                }),
                mode: 'cors'
            });
            if (response.ok) {
                return { success: true };
            }
            throw new Error("Bridge refused open drawer command");
        } catch (bridgeErr: any) {
            return { success: false, error: bridgeErr.message };
        }
    }

    async sendToThermalPrinter(printData: any) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);

            const token = localStorage.getItem('token');
            let url = `${API_BASE_URL}/printers/print-order`;
            if (this.currentBranchId) {
                url += `${url.includes('?') ? '&' : '?'}branchId=${this.currentBranchId}`;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(printData),
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (response.ok) {
                return await response.json();
            }
            throw new Error(`HTTP Error: ${response.status}`);
        } catch (err: any) {
            console.warn("⚠️ Cloud print failed, attempting local fallback bridge:", err.message);
            return this.fallbackPrintOrder(printData);
        }
    }

    async sendToPrinter(printData: any) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);

            const token = localStorage.getItem('token');
            let url = `${API_BASE_URL}/printers/print-raw`;
            if (this.currentBranchId) {
                url += `${url.includes('?') ? '&' : '?'}branchId=${this.currentBranchId}`;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(printData),
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (response.ok) {
                return await response.json();
            }
            throw new Error(`HTTP Error: ${response.status}`);
        } catch (err: any) {
            console.warn("⚠️ Cloud print raw failed, attempting local fallback bridge:", err.message);
            return this.fallbackPrintOrder(printData);
        }
    }

    async sendInvoiceToPrinter(invoiceData: any) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);

            const token = localStorage.getItem('token');
            let url = `${API_BASE_URL}/printers/print-invoice`;
            if (this.currentBranchId) {
                url += `${url.includes('?') ? '&' : '?'}branchId=${this.currentBranchId}`;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(invoiceData),
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (response.ok) {
                return await response.json();
            }
            throw new Error(`HTTP Error: ${response.status}`);
        } catch (err: any) {
            console.warn("⚠️ Cloud invoice print failed, attempting local fallback bridge:", err.message);
            return this.fallbackPrintInvoice(invoiceData);
        }
    }

    async openCashDrawer() {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);

            const token = localStorage.getItem('token');
            let url = `${API_BASE_URL}/printers/open-drawer`;
            if (this.currentBranchId) {
                url += `${url.includes('?') ? '&' : '?'}branchId=${this.currentBranchId}`;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (response.ok) {
                return await response.json();
            }
            throw new Error(`HTTP Error: ${response.status}`);
        } catch (err: any) {
            console.warn("⚠️ Cloud open drawer failed, attempting local fallback bridge:", err.message);
            return this.fallbackOpenDrawer();
        }
    }

    async getPrinters() {
        try {
            const data = await this.request(`/printers/status?t=${new Date().getTime()}`);
            if (data && data.success && data.printers) {
                localStorage.setItem('cached_printers', JSON.stringify(data.printers));
            }
            return data;
        } catch (err) {
            const cached = localStorage.getItem('cached_printers');
            if (cached) {
                return { success: true, printers: JSON.parse(cached) };
            }
            throw err;
        }
    }

    async addPrinter(printer: any) {
        return this.request('/printers/add', {
            method: 'POST',
            body: JSON.stringify(printer),
        });
    }

    async discoverPrinters() {
        return this.request('/printers/discover', {
            method: 'POST',
        });
    }

    async updatePrinterConfig(config: any) {
        return this.request('/printers/config', {
            method: 'PUT',
            body: JSON.stringify(config),
        });
    }

    async deletePrinter(id: string) {
        return this.request(`/printers/${id}`, {
            method: 'DELETE',
        });
    }

    async testPrinter(id: string) {
        return this.request(`/printers/test/${id}`, {
            method: 'POST',
        });
    }

    // Reviews
    async getReviews() {
        return this.request(`/reviews?t=${new Date().getTime()}`);
    }

    async createReview(review: any) {
        return this.request('/reviews', {
            method: 'POST',
            body: JSON.stringify(review),
        });
    }

    // Reports & Stats
    async getDashboardStats() {
        return this.request(`/reports/stats?t=${new Date().getTime()}`);
    }

    async getPayments() {
        return this.request(`/reports/payments?t=${new Date().getTime()}`);
    }

    async getHistory(view: 'day' | 'week' | 'month' = 'day') {
        return this.request(`/reports/history?view=${view}&t=${new Date().getTime()}`);
    }

    async getLocalHistory(view: 'day' | 'week' | 'month' | 'year' = 'day') {
        return this.request(`/reports/local-history?view=${view}&t=${new Date().getTime()}`);
    }

    // ── Local daily reports saved on the server machine ──
    async getLocalReports() {
        return this.request(`/reports/local-reports?t=${new Date().getTime()}`);
    }

    async generateLocalReport(branchId = 'gombita-1') {
        return this.request('/reports/local-reports/generate', {
            method: 'POST',
            body: JSON.stringify({ branchId }),
        });
    }

    getLocalReportDownloadUrl(fileName: string): string {
        return `${API_BASE_URL}/reports/local-reports/${encodeURIComponent(fileName)}`;
    }

    async triggerSync() {
        return this.request('/sync/trigger');
    }

    async getDbStatus() {
        return this.request('/db-status');
    }

    async reconnectDb() {
        return this.request('/db-reconnect', { method: 'POST' });
    }

    // Instagram followers count (cached by server — safe to call on page load)
    async getInstagramFollowers(): Promise<{ followers_count: number; fetched_at: string } | null> {
        try {
            return await this.request('/instagram/followers');
        } catch {
            return null;
        }
    }

}

export const apiService = new APIService();
export default apiService;
